import React from 'react';
import { Button, ButtonProps } from './Button';
import { classes, keyframes, style } from 'typestyle';
import { ButtonProminence, ButtonSize } from './buttonStyles';
import { flexRoot, centerCenter } from 'csstips';
import { px } from 'csx';
import { colorGreyAccent, rebrand } from '../../../../theme/color';

const shimmer = keyframes({
  '0%': {
    transform: 'translateX(-100%)',
  },
  '100%': {
    transform: 'translateX(100%)',
  },
});

const styles = {
  button: style(flexRoot, centerCenter, {
    fontSize: px(16),
    padding: '6px 10px',
    fontWeight: 400,
    textTransform: 'capitalize',
    width: 'fit-content',
    $nest: {
      '&.active': {
        backgroundColor: rebrand.primary[50].toString(),
        color: rebrand.contrast[90].toString(),
        $nest: {
          '&:hover': {
            backgroundColor: rebrand.primary[50].toString(),
          },
        },
      },
    },
  }),
  buttonSmall: style({
    minHeight: '24px',
    maxHeight: '24px',
    fontSize: px(12),
    padding: '4px 8px',
    color: rebrand.neutralOnLight[80].toHexString(),
    backgroundColor: rebrand.neutralOnDark[10].toHexString(),
    $nest: {
      '&:hover': {
        backgroundColor: colorGreyAccent.toString(),
      },
    },
  }),
  circle: style({
    borderRadius: '100px',
  }),
  icon: style({
    marginRight: '4px',
  }),
  iconRight: style({
    marginLeft: '4px',
  }),
  scrollOnHover: style({
    $nest: {
      ':hover > &': {
        display: 'inline-block',
        transitionProperty: 'transform',
        transitionTimingFunction: 'linear',
      },
    },
  }),
  loadingStyle: style({
    position: 'relative',
    overflow: 'hidden',
    $nest: {
      '&::after': {
        content: "''",
        position: 'absolute',
        top: 0,
        left: '-100%',
        width: '200%',
        height: '100%',
        background: 'linear-gradient(to right, transparent, rgba(140, 138, 138, 0.2) 50%, transparent)',
        animation: `${shimmer} 1.5s infinite`,
      },
    },
  }),
  hiddenText: style({
    visibility: 'hidden',
  }),
};

type TagButtonStyle = 'normal' | 'small' | 'circular';

export type TagButtonProps = ButtonProps & {
  text: string;
  icon?: any;
  iconRight?: any;
  active?: boolean;
  tagStyle?: TagButtonStyle;
  onClick: () => void;
  hideHoverState?: boolean;
  loading?: boolean;
  ariaLabel?: string;
};

type TagButtonState = {
  scrollClass: string | null;
};

export class TagButton extends React.Component<TagButtonProps, TagButtonState> {
  container: HTMLElement;

  state = {
    scrollClass: null,
  };

  setShouldScroll() {
    if (!this.container) return;
    const { scrollWidth, clientWidth } = this.container;
    // The button has a 10px padding, so the tag value would still be visible below clientWidth + 10
    if (scrollWidth <= clientWidth + 10) {
      this.setState({ scrollClass: null });
      return;
    }
    const difference = scrollWidth - clientWidth;
    // Normally generating styles on the fly is a no-no as it can be quite intensive, but as the value is unknown we have no choice
    const scrollClass = style({
      // 13px per second feels like a comfortable scrolling speed
      transitionDuration: Math.floor(difference / 13) + 's',
      $nest: {
        ':hover > &': {
          transform: `translateX(-${difference}px)`,
        },
      },
    });
    // Update the scrollClass state only if it has changed to prevent unnecessary re-renders
    if (this.state.scrollClass !== scrollClass) {
      this.setState({ scrollClass });
    }
  }

  componentDidMount(): void {
    this.setShouldScroll();
  }

  componentDidUpdate(prevProps: TagButtonProps, prevState: TagButtonState): void {
    // Decide if we should scroll the tag text if the text changes due to filtering on the trackline
    if (prevProps.text !== this.props.text || prevProps.className !== this.props.className) {
      this.setShouldScroll();
    }
  }

  render() {
    const {
      text,
      icon,
      iconRight,
      active = false,
      tagStyle = 'normal',
      onClick,
      className,
      loading,
      ariaLabel,
      ...props
    } = this.props;
    const { scrollClass } = this.state;

    const buttonStyles = classes(
      styles.button,
      tagStyle === 'circular' && styles.circle,
      tagStyle === 'small' && styles.buttonSmall,
      className,
      loading && styles.loadingStyle,
      active && 'active'
    );

    return (
      <Button
        className={buttonStyles}
        prominence={ButtonProminence.SECONDARY}
        size={ButtonSize.SMALL}
        onClick={this.props.onClick}
        aria-label={ariaLabel}
        {...props}
      >
        {icon && <span className={styles.icon}> {icon}</span>}
        <span
          ref={(ref) => (this.container = ref)}
          className={classes(styles.scrollOnHover, scrollClass, loading && styles.hiddenText)}
        >
          {text}
        </span>
        {iconRight && <span className={styles.iconRight}> {iconRight}</span>}
      </Button>
    );
  }
}
